/**
 * @fileoverview Definições de tipos para o sistema de formatação e parsing
 * @module Types
 * @version 1.0.0
 */

/**
 * Categorias suportadas para formatação de dados
 *
 * @public
 */
export type FormatCategory =
  /** Formato brasileiro de data: dd/MM/yyyy */
  | "date:BR"
  /** Formato numérico de data: yyyyMMdd */
  | "date:YYYYMMDD"
  /** Formato de data para células (igual ao BR): dd/MM/yyyy */
  | "date:CELL"
  /** Formato monetário brasileiro: R$ 1.234,56 */
  | "currency"
  /** Formato de porcentagem: 12.34% */
  | "percentage"
  /** Formato de CNPJ: 12.345.678/0001-95 */
  | "cnpj"
  /** Capitalização de nomes próprios: Fulano Silva */
  | "capitalize"
  /** Iniciais de nomes: FJ */
  | "initials";

/**
 * Categorias suportadas para parsing de dados
 *
 * @public
 */
export type ParseCategory =
  /** Parse de data brasileira: dd/MM/yyyy */
  | "date:BR"
  /** Parse de data numérica: yyyyMMdd */
  | "date:YYYYMMDD"
  /** Parse específico para datas do Protheus */
  | "date:PROTHEUS"
  /** Parse de números com formato brasileiro: R$ 1.234,56 -> 1234.56 */
  | "float"
  /** Extração de dígitos: abc123def -> 123 */
  | "digits"
  /** Parse de logs de erro do sistema Protheus */
  | "protheus:error";

/**
 * Opções de configuração para operações de formatação
 *
 * @public
 */
export interface FormatOptions {
  /** Categoria de formatação a ser aplicada */
  category: FormatCategory;
  /** Número de casas decimais (para currency e percentage) */
  decimals?: number;
  /** Número máximo de iniciais (para initials) */
  maxInitials?: number;
  /** Valor padrão retornado em caso de falha na formatação */
  fallback?: string;
}

/**
 * Opções de configuração para operações de parsing
 *
 * @public
 */
export interface ParseOptions {
  /** Categoria de parsing a ser aplicada */
  category: ParseCategory;
  /** Parte do horário para composição de data completa (formato HHMMSS) */
  timePart?: string;
  /** Define se valores nulos são permitidos no retorno */
  allowNull?: boolean;
}

/**
 * Estrutura de dados para erros parseados do sistema Protheus ERP
 *
 * @public
 */
export interface ProtheusError {
  /** Código de erro identificador */
  code: string;
  /** Número da nota fiscal */
  nf?: string;
  /** Série da nota fiscal */
  serie?: string;
  /** Código do fornecedor */
  fornecedor?: string;
  /** Código da loja */
  loja?: string;
  /** Data de emissão no formato dd/MM/yyyy */
  emissao: string;
}

/**
 * Tipo union para valores que podem ser formatados
 *
 * @public
 */
export type FormattableValue = string | number | Date | null | undefined;

/**
 * Tipo union para valores que podem ser parseados
 *
 * @public
 */
export type ParseableValue = string | null | undefined;

/**
 * Resultado possível de operações de parsing
 *
 * @public
 */
export type ParseResult<T = unknown> = T | null;

/**
 * Configurações opcionais para funções de formatação de data
 *
 * @public
 */
export interface DateFormatOptions {
  /** Valor padrão retornado em caso de falha */
  fallback?: string;
}

/**
 * Configurações opcionais para funções de formatação numérica
 *
 * @public
 */
export interface NumberFormatOptions {
  /** Número de casas decimais */
  decimals?: number;
  /** Valor padrão retornado em caso de falha */
  fallback?: string;
}

/**
 * Configurações opcionais para funções de formatação de string
 *
 * @public
 */
export interface StringFormatOptions {
  /** Número máximo de iniciais (para função initials) */
  maxInitials?: number;
  /** Valor padrão retornado em caso de falha */
  fallback?: string;
}

/**
 * Configurações para operações de parsing que permitem valores nulos
 *
 * @public
 */
export interface NullableParseOptions {
  /** Define se valores nulos são permitidos */
  allowNull?: boolean;
}

/**
 * Configurações específicas para parsing de datas YYYYMMDD
 *
 * @public
 */
export interface DateYYYYMMDDParseOptions extends NullableParseOptions {
  /** Parte do horário para composição de data completa */
  timePart?: string;
}
